/**
 * Search overlay.
 *
 * A panel that drops from y=0 and sits BEHIND the header bar, so its background
 * image runs unbroken from above the logo all the way down. It holds one rule
 * with a body-copy placeholder and an arrow at its right end.
 *
 * WHERE THE WORK IS SPLIT
 *
 *   Liquid — the form itself. Enter in the field and a click on the arrow both
 *            submit natively; nothing here touches submission, and the panel's
 *            hidden `type=product` input is what scopes the results. See
 *            sections/search-overlay.liquid.
 *   CSS    — how it looks and where it sits: the clip reveal, the stacking order,
 *            the header's colour swap and logo. See the comment block in that
 *            section and "Header state machine" in assets/critical.css.
 *   here   — the state itself, and the three things state drags along with it:
 *            the ARIA contract, `inert`, and where focus goes.
 *
 * ONE STATE ATTRIBUTE PER CONCERN. `data-open` goes on the panel; `data-search-open`
 * goes on <site-header> and nowhere else. Same discipline as the mobile menu, the
 * mega menu and the cart drawer, so the bar and the panel can never disagree about
 * whether something is open.
 *
 * A DISCLOSURE, NOT A DIALOG. There is no focus trap, no page-inert and no scroll
 * lock. Two consequences, both chosen rather than overlooked:
 *
 *   - IT DOES NOT CLOSE ON SCROLL, unlike assets/mega-menu.js. The panel is fixed
 *     at y=0 and travels with the sticky bar, so scrolling is not a signal that
 *     the visitor is done with it.
 *   - TAB PAST THE ARROW WALKS INTO THE PAGE BEHIND THE PANEL, which is visually
 *     covered at the top. This is the same trade-off the mega menu already
 *     carries. Containing focus would mean inerting the page, which makes this a
 *     modal — explicitly not what was asked for.
 *
 * WITHOUT THIS SCRIPT THE TRIGGERS STILL WORK. Both are real links to
 * routes.search_url, and that is the fallback: a failure here is a Search link
 * that navigates instead of opening, not a dead control. It is why they stay
 * <a href> rather than becoming buttons.
 *
 * WHY THIS IS A REAL ASSET FILE AND NOT A {% javascript %} BLOCK: same reason as
 * assets/site-header.js, mobile-menu.js, mega-menu.js and cart-drawer.js — section
 * JS is bundled and injected by Shopify in an order we cannot see or control, and
 * it was measured NOT running at all on the live store.
 *
 * LOAD ORDER: after mega-menu.js and BEFORE cart-drawer.js. The cart closes this
 * panel by calling close() on the element directly, so this must be upgraded first.
 */
if (!customElements.get('search-overlay')) {
  customElements.define(
    'search-overlay',
    class SearchOverlay extends HTMLElement {
      connectedCallback() {
        /**
         * Looked up globally, not with closest(): this panel is its own section
         * and is a SIBLING of the header's section inside the header group, not a
         * descendant of <site-header>. The triggers are found by their data
         * attribute rather than by position, the same convention as
         * data-menu-trigger and data-cart-trigger.
         *
         * THERE ARE TWO OF THEM. The header renders the search control twice —
         * a glyph before the cart on mobile, a label after it on desktop — each
         * display:none at the other breakpoint, so that the tab order matches
         * what is on screen at both. See the note in sections/header.liquid.
         */
        /**
         * NOT CACHED ON `this`, DELIBERATELY — same reasoning as
         * assets/mobile-menu.js. The header is its own section, so the theme
         * editor can re-render it independently; a reference taken here would
         * then point at a detached element and `data-search-open` would be
         * written to nothing, leaving the bar unchanged while the panel opens.
         * currentHeader() re-queries at each use. The local is only for the
         * fail-loudly check below.
         */
        var header = document.querySelector('site-header');
        this.triggers = Array.prototype.slice.call(
          document.querySelectorAll('[data-search-trigger]')
        );

        /**
         * Fail loudly and change nothing. The panel keeps the `inert` and the
         * clipped state it was rendered with, so a broken wiring is a Search link
         * that simply navigates rather than a screen-reader trap sitting under
         * the header.
         */
        if (!header || !this.triggers.length) {
          console.warn(
            '[search-overlay] no <site-header> or [data-search-trigger] found; overlay disabled'
          );
          return;
        }

        this.input = this.querySelector('input[type="search"]');
        this.scrim = this.querySelector('[data-search-scrim]');
        this.isOpen = false;

        /** The trigger that opened the panel, so Escape can hand focus back. */
        this.openedBy = null;

        this.handleTriggerClick = this.handleTriggerClick.bind(this);
        this.handleScrimClick = this.handleScrimClick.bind(this);
        this.handleKeydown = this.handleKeydown.bind(this);

        var self = this;
        this.triggers.forEach(function (trigger) {
          trigger.addEventListener('click', self.handleTriggerClick);
        });

        /**
         * CLICK-OUTSIDE IS ONE LISTENER ON THE SCRIM, not a document-level handler
         * testing whether the click landed inside the panel. The scrim IS
         * everything outside the panel — it covers the viewport and sits under it —
         * so there is no geometry to get wrong and nothing to go stale. Same
         * approach as assets/cart-drawer.js.
         *
         * The header bar is above the scrim at z-50 and therefore not covered by
         * it, which is correct: clicking Search there already toggles this shut,
         * and clicking Cart opens the drawer, which closes it too.
         */
        if (this.scrim) this.scrim.addEventListener('click', this.handleScrimClick);

        document.addEventListener('keydown', this.handleKeydown);
      }

      disconnectedCallback() {
        var self = this;

        if (this.triggers) {
          this.triggers.forEach(function (trigger) {
            trigger.removeEventListener('click', self.handleTriggerClick);
          });
        }

        if (this.scrim) this.scrim.removeEventListener('click', this.handleScrimClick);

        document.removeEventListener('keydown', this.handleKeydown);

        /**
         * A panel torn down while open — the theme editor re-rendering the header
         * group is the realistic case — would otherwise leave the bar transparent,
         * shadowless and wearing the panel's text colour, with nothing left alive
         * to put it back.
         */
        var openHeader = this.currentHeader();
        if (this.isOpen && openHeader) {
          openHeader.removeAttribute('data-search-open');
          this.setTriggersExpanded(false);
        }
      }

      /* ------------------------------------------------------------------ */
      /* Open / close                                                        */
      /* ------------------------------------------------------------------ */

      /**
       * Modified clicks are left alone so Search can still be opened in a new tab,
       * which is exactly what the underlying link does.
       */
      handleTriggerClick(event) {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

        event.preventDefault();

        if (this.isOpen) {
          this.close();
          return;
        }

        /**
         * WAS THIS A REAL CLICK, OR ENTER ON THE LINK?
         *
         * `detail` is the click count, and a keyboard-activated click reports 0 —
         * the one dependable way to tell the two apart, since a synthesised click
         * carries no pointer information. It decides whether the field shows a
         * focus ring; see the `data-quiet` note in sections/search-overlay.liquid.
         */
        this.open(event.currentTarget, event.detail !== 0);
      }

      /**
       * Clicking anywhere outside the panel. Focus is inside a region that is about
       * to go inert, so it has to be moved somewhere deliberate or the browser drops
       * it to <body> — the same reasoning as Escape below, and the same resolution.
       *
       * Focus goes back to the trigger even though this was a pointer action. That
       * is not a stray ring: `returnFocus()` calls plain `.focus()` on a link the
       * visitor just clicked away from, and the trigger's own `:focus-visible`
       * rule will not match a pointer-driven focus.
       */
      handleScrimClick() {
        this.close();
        this.returnFocus();
      }

      /**
       * @param {Element} [trigger] - The control that opened it, for focus return
       * @param {boolean} [quiet] - Suppress the field's focus ring (pointer opens)
       */
      open(trigger, quiet) {
        if (this.isOpen) return;

        /**
         * ORDER IS LOAD-BEARING, for the same reason it is in cart-drawer.js: the
         * mobile menu's setPageInert() makes every body child inert except its own
         * and the header's — and in the flat DOM shape this panel is its own body
         * child, so it would be inert too. Closing the menu first restores those
         * attributes before this panel needs to be interactive.
         */
        document.querySelector('mobile-menu')?.close?.();
        document.querySelector('mega-menu')?.close?.();
        document.querySelector('cart-drawer')?.close?.();

        /**
         * The collection's filter tray. Not a stacking or inert conflict — it is a
         * disclosure at z-30, well under this panel — but leaving a tray hanging
         * open behind the search field is untidy.
         */
        document.querySelector('filter-drawer')?.close?.();

        this.openedBy = trigger || this.triggers[0];

        /**
         * Set BEFORE the focus() below, so the rule is already in place by the time
         * the browser decides the field matches `:focus-visible`. Setting it after
         * would let the ring paint for a frame.
         *
         * Defaults to quiet when the caller said nothing — the only other callers
         * are other components, none of which are a keyboard action on this panel.
         */
        this.toggleAttribute('data-quiet', quiet !== false);

        this.setOpen(true);

        /**
         * Focus the field, which is the only reason anyone opened this. Unlike the
         * cart drawer — which deliberately focuses its panel rather than a control,
         * to avoid parking a focus ring on Close — a text input is the thing being
         * offered, and a caret in it is the expected result of clicking Search.
         *
         * `preventScroll` because the panel is fixed at y=0: without it a browser
         * that decides the field is out of view would scroll the page behind to
         * "reveal" something that never moved.
         */
        if (this.input) this.input.focus({ preventScroll: true });
      }

      close() {
        if (!this.isOpen) return;
        this.setOpen(false);
      }

      /** The single funnel. Nothing else touches state. */
      setOpen(next) {
        if (next === this.isOpen) return;
        this.isOpen = next;

        this.toggleAttribute('data-open', next);

        /**
         * The panel stays in the render tree to animate, and a clipped,
         * transparent box still holds a focusable field and a submit button.
         * `inert` is what actually removes them from the tab order and the
         * accessibility tree.
         */
        this.toggleAttribute('inert', !next);

        var header = this.currentHeader();
        if (header) header.toggleAttribute('data-search-open', next);
        this.setTriggersExpanded(next);
      }

      /**
       * The live <site-header>, re-queried rather than cached — see the note in
       * connectedCallback. Callers must handle null.
       */
      currentHeader() {
        return document.querySelector('site-header');
      }

      setTriggersExpanded(next) {
        this.triggers.forEach(function (trigger) {
          trigger.setAttribute('aria-expanded', String(next));
        });
      }

      /* ------------------------------------------------------------------ */
      /* Escape hatches                                                      */
      /* ------------------------------------------------------------------ */

      /**
       * Escape closes and hands focus back to whichever trigger opened it — focus
       * is inside a panel that is about to go inert, so it has to be moved
       * somewhere deliberate or the browser drops it to <body>.
       *
       * NO `escaped` GUARD IS NEEDED HERE, unlike assets/mega-menu.js. That one
       * opens on `:focus-visible`, so focusing its trigger re-opened the panel and
       * Escape appeared to do nothing. This opens on click only, so returning
       * focus is inert.
       */
      handleKeydown(event) {
        if (!this.isOpen) return;

        /**
         * THE MOMENT THE VISITOR TABS, THEY ARE ON THE KEYBOARD and every focus
         * indicator from here on is theirs by right. This is what keeps the quiet
         * state from being a blanket removal: it only ever covers the one scripted
         * focus that the panel itself performed.
         */
        if (event.key === 'Tab') {
          this.removeAttribute('data-quiet');
          return;
        }

        if (event.key !== 'Escape') return;

        this.close();
        this.returnFocus();
      }

      /**
       * Hand focus back to whichever trigger opened the panel. It is inside a
       * region that has just gone inert, so it has to go somewhere deliberate or
       * the browser drops it to <body> and a keyboard visitor loses their place.
       *
       * The remembered trigger may be the one that is display:none at the current
       * breakpoint — the viewport can be resized while the panel is open, and the
       * header renders the search control twice for exactly that reason. Focusing a
       * hidden element silently does nothing, so fall back to whichever is rendered.
       */
      returnFocus() {
        var trigger = this.openedBy;

        if (trigger && trigger.offsetParent === null) trigger = null;
        if (!trigger) {
          trigger = this.triggers.find(function (candidate) {
            return candidate.offsetParent !== null;
          });
        }

        if (trigger) trigger.focus();
      }
    }
  );
}
